import type { AxiosError } from 'axios'
import axios from 'axios'

import { API_ADDRESS } from '../constants'

import { LocalStorageKey } from '@msteams/types/common'

const token = localStorage.getItem(LocalStorageKey.mstoken)

const axiosInstance = axios.create({
  baseURL: API_ADDRESS,
  headers: {
    Authorization: token,
  },
  withCredentials: true,
})

axiosInstance.interceptors.request.use((req) => {
  const authToken = localStorage.getItem(LocalStorageKey.mstoken)
  const lsToken = authToken || ''
  axiosInstance.defaults.headers.Authorization = lsToken
  req.headers.Authorization = lsToken
  return req
})

// An expired Microsoft token makes the backend raise NotAuthenticated, but DRF
// rewrites that to 403 because the Teams views offer no WWW-Authenticate header.
// Clearing only on 401 therefore left the dead token in localStorage forever:
// the request interceptor above replayed it on every retry, so the user was
// pinned on the Sign In screen until they wiped the Teams cache by hand.
//
// A blanket clear on 403 would be wrong — RBAC denials and suspended accounts
// also answer 403, and those must not silently sign the user out. They are
// distinguishable by body: suspension returns our own `error_code` envelope,
// and a permission denial returns DRF's "You do not have permission…" text.
// Only the authentication message clears the token.
const NOT_AUTHENTICATED_DETAIL = 'Authentication credentials'

const isExpiredTokenResponse = (err: AxiosError<{ detail?: string }>): boolean => {
  const status = err?.response?.status

  if (status === 401) return true
  if (status !== 403) return false

  const detail = err?.response?.data?.detail

  return typeof detail === 'string' && detail.startsWith(NOT_AUTHENTICATED_DETAIL)
}

axiosInstance.interceptors.response.use(undefined, (err: AxiosError<{ detail?: string }>) => {
  if (isExpiredTokenResponse(err)) {
    localStorage.removeItem(LocalStorageKey.mstoken)
    localStorage.removeItem(LocalStorageKey.mstokenExpiresOn)
    axiosInstance.defaults.headers.Authorization = ''
  }

  throw err
})

export default axiosInstance
